import i18next from 'i18next'
import type { ReactNode } from 'react'
import { toast as sonnerToast } from 'sonner'

interface ToastOptions {
  message: string | ReactNode
  description?: string | ReactNode
  variables?: Record<string, unknown>
  descriptionVars?: Record<string, unknown>
}

function isStringValue(value: unknown): value is string {
  return typeof value === 'string'
}

function translateIfString(
  content: string | ReactNode,
  variables?: Record<string, unknown>,
): string | ReactNode {
  if (isStringValue(content)) {
    return i18next.t(content, variables as Record<string, string>)
  }
  return content
}

function prepareToastContent(options: ToastOptions) {
  const { message, description, variables, descriptionVars } = options

  const translatedMessage = translateIfString(message, variables)

  const translatedDescription = description
    ? translateIfString(description, descriptionVars || variables)
    : undefined

  return {
    translatedMessage,
    translatedDescription,
  }
}

const toast = {
  success: (options: ToastOptions) => {
    const { translatedMessage, translatedDescription } = prepareToastContent(options)

    return sonnerToast.success(translatedMessage, {
      description: translatedDescription,
    })
  },

  error: (options: ToastOptions) => {
    const { translatedMessage, translatedDescription } = prepareToastContent(options)

    return sonnerToast.error(translatedMessage, {
      description: translatedDescription,
    })
  },

  warning: (options: ToastOptions) => {
    const { translatedMessage, translatedDescription } = prepareToastContent(options)

    return sonnerToast.warning(translatedMessage, {
      description: translatedDescription,
    })
  },

  info: (options: ToastOptions) => {
    const { translatedMessage, translatedDescription } = prepareToastContent(options)

    return sonnerToast.info(translatedMessage, {
      description: translatedDescription,
    })
  },

  dismiss: sonnerToast.dismiss,
}

export { toast }
export default toast
